import _regeneratorRuntime from "/var/lib/jenkins/jobs/workspace/tradingblock_prod/node_modules/babel-preset-react-app/node_modules/@babel/runtime/regenerator";
import _asyncToGenerator from "/var/lib/jenkins/jobs/workspace/tradingblock_prod/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";
import _slicedToArray from "/var/lib/jenkins/jobs/workspace/tradingblock_prod/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { useRef, useCallback } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { withMiddleware, useStateWithDeps, UNDEFINED, serialize, mergeObjects, getTimestamp, useIsomorphicLayoutEffect } from 'swr/_internal';

var mutation = function mutation() {
  return function (key, fetcher) {
    var config = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

    var _useSWRConfig = useSWRConfig(),
        mutate = _useSWRConfig.mutate;

    var keyRef = useRef(key);
    var fetcherRef = useRef(fetcher); // Ditch all mutation results that happened earlier than this timestamp.

    var ditchMutationsUntilRef = useRef(0);

    var _useStateWithDeps = useStateWithDeps({
      data: UNDEFINED,
      error: UNDEFINED,
      isMutating: false
    }),
        _useStateWithDeps2 = _slicedToArray(_useStateWithDeps, 3),
        stateRef = _useStateWithDeps2[0],
        stateDependencies = _useStateWithDeps2[1],
        setState = _useStateWithDeps2[2];

    var currentState = stateRef.current;
    var trigger = useCallback(
    /*#__PURE__*/
    function () {
      var _ref = _asyncToGenerator(
      /*#__PURE__*/
      _regeneratorRuntime.mark(function _callee(arg, opts) {
        var _serialize, _serialize2, serializedKey, resolvedKey, options, mutationStartedAt, data;

        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _serialize = serialize(keyRef.current), _serialize2 = _slicedToArray(_serialize, 2), serializedKey = _serialize2[0], resolvedKey = _serialize2[1];

                if (fetcherRef.current) {
                  _context.next = 3;
                  break;
                }

                throw new Error('Can’t trigger the mutation: missing fetcher.');

              case 3:
                if (serializedKey) {
                  _context.next = 5;
                  break;
                }

                throw new Error('Can’t trigger the mutation: missing key.');

              case 5:
                // Disable cache population by default.
                options = mergeObjects(mergeObjects({
                  populateCache: false,
                  throwOnError: true
                }, config), opts); // Trigger a mutation, and also track the timestamp. Any mutation that happened
                // earlier this timestamp should be ignored.

                mutationStartedAt = getTimestamp();
                ditchMutationsUntilRef.current = mutationStartedAt;
                setState({
                  isMutating: true
                });
                _context.prev = 9;
                _context.next = 12;
                return mutate(serializedKey, fetcherRef.current(resolvedKey, {
                  arg: arg
                }), // We must throw the error here so we can catch and update the states.
                mergeObjects(options, {
                  throwOnError: true
                }));

              case 12:
                data = _context.sent;

                // If it's reset after the mutation, we don't broadcast any state change.
                if (ditchMutationsUntilRef.current <= mutationStartedAt) {
                  setState({
                    data: data,
                    isMutating: false,
                    error: undefined
                  });
                  options.onSuccess == null ? void 0 : options.onSuccess(data, serializedKey, options);
                }

                return _context.abrupt("return", data);

              case 17:
                _context.prev = 17;
                _context.t0 = _context["catch"](9);

                if (!(ditchMutationsUntilRef.current <= mutationStartedAt)) {
                  _context.next = 24;
                  break;
                }

                setState({
                  error: _context.t0,
                  isMutating: false
                });
                options.onError == null ? void 0 : options.onError(_context.t0, serializedKey, options);

                if (!options.throwOnError) {
                  _context.next = 24;
                  break;
                }

                throw _context.t0;

              case 24:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, null, [[9, 17]]);
      }));

      return function (_x, _x2) {
        return _ref.apply(this, arguments);
      };
    }(), // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    var reset = useCallback(function () {
      ditchMutationsUntilRef.current = getTimestamp();
      setState({
        data: UNDEFINED,
        error: UNDEFINED,
        isMutating: false
      }); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useIsomorphicLayoutEffect(function () {
      keyRef.current = key;
      fetcherRef.current = fetcher;
    }); // We don't return `mutate` here as it can be pretty confusing (e.g. people
    // calling `mutate` but they actually mean `trigger`).
    // And also, `mutate` relies on the useSWR hook to exist too.

    return {
      trigger: trigger,
      reset: reset,

      get data() {
        stateDependencies.data = true;
        return currentState.data;
      },

      get error() {
        stateDependencies.error = true;
        return currentState.error;
      },

      get isMutating() {
        stateDependencies.isMutating = true;
        return currentState.isMutating;
      }

    };
  };
};
/**
 * A hook to define and manually trigger remote mutations like POST, PUT, DELETE and PATCH use cases.
 *
 * @link https://swr.vercel.app/docs/mutation
 * @example
 * ```jsx
 * import useSWRMutation from 'swr/mutation'
 *
 * const {
 *   data,
 *   error,
 *   trigger,
 *   reset,
 *   isMutating
 * } = useSWRMutation(key, fetcher, options?)
 * ```
 */


var index = withMiddleware(useSWR, mutation);
export { index as default };